<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :closable="false"
    :draggable="false"
    :style="{ width: '34rem', maxWidth: '100%' }"
    class="p-0 m-0"
    :pt="{
      mask: {
        style: 'backdrop-filter: blur(1px)',
      },
    }"
  >
    <!-- HEADER -->
    <template #header>
      <div class="flex items-center gap-3">
        <i class="pi pi-exclamation-triangle text-red-500 text-lg" />
        <div class="flex flex-col">
          <span class="font-semibold text-base"> There was an error processing your request </span>
          <span class="text-xs text-gray-500">
            Please review the details below and choose an option.
          </span>
        </div>
      </div>
    </template>

    <!-- BODY -->
    <div class="px-1 py-1 space-y-4 text-sm">
      <!-- Error details -->
      <section class="space-y-2">
        <div class="flex flex-wrap gap-1">
          <span class="font-semibold">Issue accessing:</span>
          <span class="italic break-all">
            {{ errorUrl || "Unknown location" }}
          </span>
        </div>

        <div class="flex flex-wrap gap-1">
          <span class="font-semibold" :class="errorStatus ? 'text-red-600' : 'text-gray-700'">
            Error code:
          </span>
          <span :class="errorStatus ? 'text-red-600' : 'text-gray-500'">
            {{ errorStatus ?? "Unknown" }}
          </span>
        </div>

        <div class="flex flex-wrap gap-1">
          <span class="font-semibold text-gray-700"> Message: </span>
          <span class="text-gray-500">
            {{ errorMessage ?? "Unknown" }}
          </span>
        </div>

        <div class="flex flex-wrap gap-1">
          <span class="font-semibold text-gray-700"> Correlation Id: </span>
          <span class="text-gray-500">
            {{ correlationId ?? "Unknown" }}
          </span>
        </div>
      </section>

      <!-- Suggestions -->
      <section class="space-y-2">
        <p class="font-semibold m-0">You can try:</p>
        <ul class="list-disc pl-5 space-y-1">
          <li>Send the Resume Builder team an email about this issue.</li>
          <li>Refresh the page.</li>
          <li>Return to the home page.</li>
        </ul>
      </section>
    </div>

    <!-- FOOTER -->
    <template #footer>
      <div class="flex flex-wrap items-center gap-3 w-full">
        <router-link to="/resumes" class="inline-block">
          <Button
            label="Home"
            outlined
            severity="info"
            class="rounded-md px-3 py-2"
            @click="
              () => {
                isVisible = false;
                resetLists();
              }
            "
          />
        </router-link>

        <Button
          label="Refresh"
          outlined
          severity="info"
          icon="pi pi-refresh"
          class="rounded-md px-3 py-2"
          @click="
            () => {
              refreshPage();
              resetLists();
            }
          "
        />

        <Button
          label="Email"
          outlined
          severity="info"
          icon="pi pi-send"
          class="rounded-md px-3 py-2"
          :disabled="sendingMail"
          @click="sendEmail"
        />

        <Button
          outlined
          label="Close"
          severity="danger"
          icon="pi pi-times"
          class="ml-auto rounded-md px-3 py-2"
          @click="
            () => {
              isVisible = false;
              resetLists();
            }
          "
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, onBeforeUnmount } from "vue";
import API from "../../core/services/axios";
import { useRouter } from "vue-router";
import { useAppContextStore } from "@/core/store";
import { useEnvironmentFlags } from "@/core/composables/useEnvironmentFlags";
import { useEmailApi } from "@/core/composables/useEmailApi";
import { useEmailNotifications } from "@/shared/composables/useEmailNotifications";
import { useAlerts } from "@/shared/composables/useAlerts";

defineOptions({
  name: "ErrorDialog",
});

const { logMail, sendMail } = useEmailApi();
const { send } = useEmailNotifications();
const { successToast, errorToast } = useAlerts();

const contextStore = useAppContextStore();
const router = useRouter();

// --- reactive state ---
const isVisible = ref(false);
const errorStatus = ref<number | null>(null);
const correlationId = ref<string | null>(null);
const errorMessage = ref<string | null>(null);
const errorUrl = ref<string | null>(null);
const sendingMail = ref(false);

const interceptorId = ref<number | null>(null);

const { isProdEnvironment } = useEnvironmentFlags();
// --- computed context ---
const context = computed(() => contextStore.context);

// --- helpers ---
const refreshPage = () => {
  isVisible.value = false;
  router.go(0);
};

const resetLists = () => {
  errorStatus.value = null;
  correlationId.value = null;
  errorMessage.value = null;
  errorUrl.value = null;
  sendingMail.value = false;
};

const reporterName = computed(() => {
  return `${context.value.user.firstName} ${context.value.user.lastName}` || "Unknown User";
});

const reporterEmail = computed(() => {
  return context.value.user.email || "No email found";
});

const emailMessage = computed(() => {
  return (
    "Error Handler Automated Message:\n\n" +
    `${reporterName.value} experienced an error while trying to access ` +
    `${errorUrl.value ?? "Unknown"}\n\n` +
    `Error Code: ${errorStatus.value ?? "Unknown"}\n\n` +
    `Error Message: ${errorMessage.value ?? "Unknown"}\n\n Correlation id: ${correlationId.value ?? "Unknown"}\n\n` +
    `Users email: ${reporterEmail.value}`
  );
});

const sendEmail = async () => {
  sendingMail.value = true;
  await send({
    requireToEmail: true,
    beforeSend: () => {},
    isProd: isProdEnvironment.value,
    sendMailDev: sendMail,
    logMail,
    onSuccess: () => {},
    onError: () => {
      errorToast("Email Failed", "Failed to send email to Resume Builder team. Please try again.");
    },
    afterSuccess: () => {
      successToast(
        "Email Sent",
        "An email has been sent to the Resume Builder team about this issue."
      );
    },

    emailJs: {
      serviceId: import.meta.env.VITE_APP_EMAILJS_SERVICE,
      templateId: import.meta.env.VITE_APP_EMAILJS_NOTIFICATION_TEMPLATE,
      publicKey: import.meta.env.VITE_APP_EMAILJS_APP,
    },

    buildTemplateParams: () => {
      const message = emailMessage.value;

      const toEmail: string = import.meta.env.VITE_APP_RESUMEBUILDER_EMAIL;

      return {
        from_name: "Error Handler",
        to_name: "ResumeBuilder Team",
        to_email: toEmail,
        message,
      };
    },

    buildMessageToLog: () => {
      const body = emailMessage.value;

      return {
        fromName: "Error Handler",
        to: import.meta.env.VITE_APP_RESUMEBUILDER_EMAIL,
        body,
        subject: "ResumeBuilder Error",
      };
    },
  });
  isVisible.value = false;
  sendingMail.value = false;
};

onBeforeMount(() => {
  interceptorId.value = API.interceptors.response.use(
    (response) => response,
    (error) => {
      const status: number | undefined = error?.response?.status;
      const correlation: string | undefined = error?.response?.data.correlationId;

      const message: string | undefined = error?.response?.data.message;

      if (status === 404) {
        router.push("/");
      } else if (status === 401) {
        // Possible reload loop spot if issues appear in future
        location.reload();
      } else if (status) {
        // Non-2xx errors: show dialog
        isVisible.value = true;
        errorStatus.value = status;
        correlationId.value = correlation ?? null;
        errorMessage.value = message ?? null;
        errorUrl.value = error.request?.responseURL ?? null;

        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );
});

onBeforeUnmount(() => {
  if (interceptorId.value !== null) {
    API.interceptors.response.eject(interceptorId.value);
  }
});
</script>
